"use client";

import React, { useState } from "react";
import { LocaleLink as Link } from "@/components/shared/LocaleLink";
import { useTranslations } from "@/lib/i18n-client";
import { Reveal, Stagger, StaggerItem } from "@/components/shared/motion";
import { track } from "@/lib/analytics";
import { Map, Camera, Video, Users, ArrowRight, Info } from "lucide-react";

/**
 * Coastal Freedom — VACATION USE-CASE SELECTOR (the brand's signature guided-
 * selling device). Maps / Social / Video / Family-hotspot → a recommended,
 * honestly-sourced data band, reframing the decision from "cheapest plan" to
 * "enough data for your whole trip" (research/03 user-behavior; research/07 CF1;
 * strategy §9; market-test `wf_usecase_selector_v1`). Data ranges are sourced to
 * The Guardian data-need bands (light ~3–5GB/week; streaming/video/work 10GB+)
 * cited in market-test §12 — NO fabricated metrics. Fair-use is disclosed up
 * front (CF3 trust wedge). Brand-native, claim-safe, no glass/blur/gradient.
 *
 * Analytics: fires `use_case_selected` + `hotspot_info_clicked` (market-test §10
 * required events). The use-case + experiment_id are encoded into event_id
 * (string) because the shared AnalyticsEvent schema in lib/analytics/index.ts has
 * no experiment_id field yet — proper field-based wiring is logged under
 * SHARED-CHANGES-NEEDED (do not edit the shared schema from a brand component).
 */

const EXPERIMENT_ID = "wf_usecase_selector_v1";

const trackUseCase = (id: string) => {
  try {
    track({ event_name: "use_case_selected", event_id: `use_case_selected:${id}:${EXPERIMENT_ID}:${Date.now()}` });
    if (id === "family") {
      track({ event_name: "hotspot_info_clicked", event_id: `hotspot_info_clicked:${EXPERIMENT_ID}:${Date.now()}` });
    }
  } catch {
    /* analytics is best-effort + consent-gated */
  }
};

type UseCase = {
  id: string;
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  size: string;
  detail: string;
};

export default function UseCaseSelector() {
  const { t } = useTranslations();
  const [active, setActive] = useState<string>("social");

  const cases: UseCase[] = [
    { id: "maps", icon: Map, label: t("cf.uc.maps", "Maps & messaging"), size: t("cf.uc.mapsSize", "~1–3 GB"), detail: t("cf.uc.mapsDetail", "Navigation, chat and light browsing for a short trip.") },
    { id: "social", icon: Camera, label: t("cf.uc.social", "Social & photos"), size: t("cf.uc.socialSize", "~5–10 GB"), detail: t("cf.uc.socialDetail", "Instagram, daily browsing and regular photo uploads.") },
    { id: "video", icon: Video, label: t("cf.uc.video", "Video & streaming"), size: t("cf.uc.videoSize", "10 GB+"), detail: t("cf.uc.videoDetail", "Video calls, streaming and heavier daily use.") },
    { id: "family", icon: Users, label: t("cf.uc.family", "Family hotspot"), size: t("cf.uc.familySize", "Go large"), detail: t("cf.uc.familyDetail", "Sharing with the family adds up fast — pick a high-data plan. Hotspot/tethering is supported.") },
  ];

  const select = (id: string) => {
    setActive(id);
    trackUseCase(id);
  };

  const current = cases.find((c) => c.id === active) ?? cases[1];

  return (
    <section className="container mx-auto px-4 py-10">
      <Reveal as="div" y={24} className="cf-card p-6 sm:p-8">
        <div className="text-xs font-bold text-primary uppercase tracking-wide mb-2">{t("cf.uc.eyebrow", "Find your plan size")}</div>
        <h2 className="text-2xl sm:text-3xl mb-2">{t("cf.uc.title1", "How will you use your")} <span className="cf-accent">{t("cf.uc.title2", "vacation data?")}</span></h2>
        <p className="text-muted-foreground max-w-xl mb-6">{t("cf.uc.sub", "Tell us how you travel and we'll point you to the right amount of data — so you have enough for the whole trip, not the cheapest plan.")}</p>

        <Stagger as="div" className="grid grid-cols-2 sm:grid-cols-4 gap-3" staggerChildren={0.09} amount={0.3}>
          {cases.map(({ id, icon: Icon, label }) => (
            <StaggerItem key={id} as="div" y={22}>
              <button
                type="button"
                onClick={() => select(id)}
                aria-pressed={active === id}
                className={`flex w-full flex-col items-center gap-2 rounded-xl border p-4 text-center transition-all active:scale-[0.97] hover:-translate-y-0.5 ${active === id ? "border-primary bg-primary/5" : "border-border hover:border-primary/40"}`}
              >
                <span className={`inline-flex items-center justify-center w-11 h-11 rounded-xl transition-transform duration-300 ${active === id ? "bg-primary text-primary-foreground scale-110" : "bg-primary/10 text-primary"}`}><Icon className="w-5 h-5" /></span>
                <span className="text-sm font-bold text-foreground">{label}</span>
              </button>
            </StaggerItem>
          ))}
        </Stagger>

        <div className="mt-5 rounded-xl border border-primary/30 bg-primary/5 p-5 flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="flex-1">
            <div className="text-xs font-semibold uppercase tracking-wide text-primary mb-1">{t("cf.uc.recommend", "Recommended for your trip")}</div>
            <div className="flex items-baseline gap-3">
              <span key={current.id} className="text-2xl font-extrabold text-foreground animate-fade-in-scale">{current.size}</span>
              <span className="text-sm text-muted-foreground">{current.label}</span>
            </div>
            <p className="mt-1 text-sm text-muted-foreground">{current.detail}</p>
          </div>
          <Link href="/destinations/" className="inline-flex items-center justify-center gap-2 rounded-lg bg-primary text-primary-foreground font-bold h-11 px-6 shrink-0 transition-all active:scale-[0.97] hover:bg-primary/90 hover:-translate-y-0.5">
            {t("cf.uc.cta", "Find matching plans")} <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        <p className="mt-3 flex items-start gap-2 text-xs text-muted-foreground">
          <Info className="w-3.5 h-3.5 mt-0.5 shrink-0 text-primary" />
          {t("cf.uc.fairuse", "Guidance only — real usage varies. Our large-data plans show clear fair-use terms up front, so you know exactly what you get. No fine-print 'unlimited'.")}
        </p>
      </Reveal>
    </section>
  );
}
